import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  UserPlus,
  Mail,
  MapPin,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsPage = ({ user, settings }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteClient, setDeleteClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        withCredentials: true,
      });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteClient) return;

    try {
      await axios.delete(`${API}/clients/${deleteClient.client_id}`, {
        withCredentials: true,
      });
      toast.success("Client supprimé avec succès");
      setClients(clients.filter((c) => c.client_id !== deleteClient.client_id));
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteClient(null);
    }
  };

  const filteredClients = clients.filter((client) => {
    const search = searchTerm.toLowerCase();
    return (
      client.code_client.toLowerCase().includes(search) ||
      client.nom.toLowerCase().includes(search) ||
      client.prenom.toLowerCase().includes(search) ||
      client.ville.toLowerCase().includes(search) ||
      client.email.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Clients
            </h1>
            <p className="text-muted-foreground mt-1">
              {clients.length} client{clients.length !== 1 ? "s" : ""} enregistré
              {clients.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Button asChild data-testid="new-client-button">
            <Link to="/clients/new">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau client
            </Link>
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par code, nom, ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        {/* Clients List */}
        {filteredClients.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">
                {searchTerm
                  ? "Aucun client trouvé pour cette recherche"
                  : "Aucun client enregistré"}
              </p>
              {!searchTerm && (
                <Button asChild className="mt-4" variant="outline">
                  <Link to="/clients/new">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Ajouter votre premier client
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="clients-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pl-6">Code</th>
                      <th>Nom complet</th>
                      <th>Téléphone</th>
                      <th>Ville</th>
                      <th>Situation</th>
                      <th>Résidence</th>
                      <th className="text-right pr-6">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => (
                      <tr
                        key={client.client_id}
                        data-testid={`client-row-${client.code_client}`}
                      >
                        <td className="pl-6 font-mono text-sm font-medium text-primary">
                          {client.code_client}
                        </td>
                        <td>
                          <div>
                            <p className="font-medium text-foreground">
                              {client.prenom} {client.nom}
                            </p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {client.email}
                            </p>
                          </div>
                        </td>
                        <td className="text-sm">{client.telephone}</td>
                        <td>
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {client.ville}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              client.situation_familiale === "Marié(e)" ||
                              client.situation_familiale === "Conjoint(e) de fait"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {client.situation_familiale}
                          </span>
                        </td>
                        <td className="text-sm text-muted-foreground">
                          {client.residence}
                        </td>
                        <td className="text-right pr-6">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                              data-testid={`edit-client-${client.code_client}`}
                            >
                              <Link to={`/clients/${client.client_id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setDeleteClient(client)}
                              data-testid={`delete-client-${client.code_client}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={!!deleteClient}
          onOpenChange={() => setDeleteClient(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer le client{" "}
                <span className="font-medium">
                  {deleteClient?.prenom} {deleteClient?.nom}
                </span>{" "}
                ({deleteClient?.code_client}) ? Cette action supprimera également
                tous les paiements, formulaires et transmissions associés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete">
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default ClientsPage;
